import { Outlet, useLocation } from 'react-router-dom';
import AuthBrandPanel from '../components/AuthBrandPanel';
// Local brand logo for the mobile brand strip. Vite handles the
// PNG import and returns the hashed asset URL at build time —
// no external URL involved.
import glassILogo from '../images/Glass-i_Logo.png';

// Single shared 45 / 55 shell for every public auth/onboarding route
// (/login, /signup, /signup/otp, /signup/payment, /signup/done).
//
// Desktop (lg+):
//   - 45% / 55% split via
//     `lg:grid-cols-[minmax(0,45fr)_minmax(0,55fr)]` — explicit
//     minmax(0, …) keeps either column from being widened by
//     oversized children (the default `1fr` resolves to
//     `minmax(auto, 1fr)`, which leaks).
//   - left column: blue branding panel, h-screen
//   - right column: white form/action panel, h-screen with internal
//                   overflow-y-auto + overflow-x-hidden so long
//                   content scrolls inside the panel and never
//                   escapes horizontally.
//   - the form/action panel is intentionally wider so it reads as
//     the primary action area on the page.
//
// Below lg:
//   - left panel hidden
//   - form/action panel renders FIRST in the DOM so it's the first
//     thing the user sees on a phone
//   - a slim brand strip drops in below as a visual signature
//
// Right-panel content max-width is route-aware so the form column
// stays comfortable: narrow for /login, generous for the multi-card
// /signup/payment surface, generous for the standard signup form.

function pickContentMaxW(pathname) {
  if (pathname === '/login' || pathname.startsWith('/login/')) {
    // Compact centered login card — keep inputs around ~440 px so
    // the form reads as a focused action, not a stretched panel.
    return 'max-w-md';
  }
  if (pathname.startsWith('/signup/payment')) {
    return 'max-w-3xl';
  }
  return 'max-w-3xl';
}

// Routes that render their content as a single white "lifted" card
// on the right panel. The PublicLayout tints the right-panel
// background with `bg-surface-50` for these routes so the card's
// shadow has contrast to read against. Other routes (the multi-card
// payment surface, the done screen) stay on a plain white panel.
function usesLiftedCard(pathname) {
  if (pathname === '/login' || pathname.startsWith('/login/')) return true;
  if (pathname === '/signup' || pathname === '/signup/otp') return true;
  return false;
}

export default function PublicLayout() {
  const { pathname } = useLocation();
  const innerMaxW = pickContentMaxW(pathname);
  const centerVertically =
    pathname === '/login' || pathname.startsWith('/login/');
  const liftedCard = usesLiftedCard(pathname);

  return (
    <div
      className="public-shell w-full min-h-screen lg:h-screen lg:max-h-screen lg:overflow-hidden grid grid-cols-1 lg:grid-cols-[minmax(0,45fr)_minmax(0,55fr)] overflow-x-hidden"
    >
      {/* Left blue brand panel — desktop only. min-w-0 +
          overflow-hidden prevent any oversized child (e.g. a wide
          background) from pushing the column past its 45fr share. */}
      <div className="hidden lg:flex lg:h-screen lg:max-h-screen min-w-0 overflow-hidden">
        <AuthBrandPanel />
      </div>

      {/* Right form/action panel. Renders second on desktop (right
          column) and first on mobile (since the left panel is
          `hidden lg:flex`, it doesn't take up grid space below lg).
          The double overflow-(x|y) declaration is intentional:
          overflow-y-auto alone leaves overflow-x at its default
          (visible), which would let content escape the column.
          /login uses a slightly tinted background so the white
          login card lifts visibly with its shadow; signup pages
          keep the plain white panel since they render flat. */}
      <div
        className={`lg:h-screen lg:max-h-screen lg:overflow-y-auto lg:overflow-x-hidden min-h-0 min-w-0 box-border ${
          liftedCard ? 'bg-surface-50' : 'bg-white'
        }`}
      >
        <div
          className={`mx-auto w-full max-w-full ${innerMaxW} box-border px-5 sm:px-7 lg:px-9 py-5 sm:py-6 lg:py-7 ${
            centerVertically ? 'lg:min-h-screen lg:flex lg:flex-col lg:justify-center' : ''
          }`}
        >
          <Outlet />
        </div>
      </div>

      {/* Mobile/tablet brand strip — appears AFTER the form on
          small screens so the action area is the first thing the
          user sees on a phone. Hidden on lg+ (the full brand panel
          replaces it). */}
      <div className="lg:hidden flex items-center gap-2 px-5 py-3 auth-brand-panel text-white">
        <img
          src={glassILogo}
          alt="Glass-i"
          className="h-7 w-auto object-contain max-w-[140px]"
        />
        <span className="ml-auto text-xs text-white/75 truncate">
          Built for Glass. Built for India.
        </span>
      </div>
    </div>
  );
}
